// ============================================================================
// Contracts — Send for Signature panel (E-Sign Phase 2).
// Shown when status is Ready_for_Signature / Sent_for_Signature / Signed.
// Mints a tokenized signing grant via the esign Edge Function (which stores
// the source PDF, advances status, records the CSE 'sent' event and emails
// the signer), then surfaces signer progress from signer_status + the
// append-only event trail. The manual "Mark as sent/signed" buttons in
// ApprovalPanel remain as fallback.
// ============================================================================

import { canSendContracts } from '../../shared/role-gates';
import { useCallback, useEffect, useState } from 'react';
import { Copy, Loader2, Send } from 'lucide-react';
import { ErrorState } from '../../shared/components';
import { supabase } from '../../shared/supabase';
import {
  EsignError, esignInternal,
} from '../esign';
import type { DeliveryResult, SignerTokenStatus } from '../esign';
import type { ContractRow } from './types';

interface SigningPanelProps {
  contract: ContractRow;
  role: string | null;
  currentEid: string | null;
  onAfterAction: () => void;
}

interface SignatureEvent {
  event_id: string;
  action: string;
  signer_email: string | null;
  signer_name: string | null;
  occurred_at: string;
}


const ACTION_LABELS: Record<string, string> = {
  sent: 'Sent to signer',
  viewed: 'Viewed by signer',
  identity_verified: 'Identity verified',
  consented: 'Consented to e-sign',
  signed: 'Signed',
  declined: 'Declined',
  expired: 'Link expired',
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(new Error('read_failed'));
    reader.readAsDataURL(file);
  });
}

export function SigningPanel({
  contract, role, currentEid, onAfterAction,
}: SigningPanelProps) {
  const relevant = ['Ready_for_Signature', 'Sent_for_Signature', 'Signed']
    .includes(contract.status);
  const canSend =
    canSendContracts(role) ||
    (currentEid != null && contract.created_by_eid === currentEid);

  const [signerEmail, setSignerEmail] = useState(contract.signer_email ?? '');
  const [signerName, setSignerName] = useState(contract.signer_name ?? '');
  const [signerTitle, setSignerTitle] = useState(contract.signer_title ?? '');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [manualUrl, setManualUrl] = useState<string | null>(null);
  const [delivery, setDelivery] = useState<DeliveryResult | null>(null);
  const [copied, setCopied] = useState(false);

  const [tokens, setTokens] = useState<SignerTokenStatus[]>([]);
  const [events, setEvents] = useState<SignatureEvent[]>([]);

  const refreshStatus = useCallback(async () => {
    if (!relevant || !canSend) return;
    try {
      const res = await esignInternal<{ tokens: SignerTokenStatus[] }>({
        action: 'signer_status',
        contract_id: contract.contract_id,
      });
      setTokens(res.tokens);
    } catch {
      /* status panel is best-effort */
    }
    const { data } = await supabase
      .from('contract_signature_events')
      .select('event_id, action, signer_email, signer_name, occurred_at')
      .eq('contract_id', contract.contract_id)
      .order('occurred_at', { ascending: false })
      .limit(20);
    if (data) setEvents(data as SignatureEvent[]);
  }, [contract.contract_id, relevant, canSend]);

  useEffect(() => {
    void refreshStatus();
  }, [refreshStatus]);

  if (!relevant) return null;

  const isSent = contract.status === 'Sent_for_Signature';
  const isSigned = contract.status === 'Signed';
  const hasActiveToken = tokens.some(
    (t) => !t.consumed_at && new Date(t.expires_at).getTime() > Date.now(),
  );

  async function send() {
    setBusy(true);
    setError(null);
    setManualUrl(null);
    setDelivery(null);
    try {
      let pdf_base64: string | undefined;
      if (pdfFile) pdf_base64 = await fileToBase64(pdfFile);
      const res = await esignInternal<{
        ok: boolean;
        token_id: string;
        delivery: DeliveryResult;
        signing_url?: string;
      }>({
        action: 'send',
        contract_id: contract.contract_id,
        signer_email: signerEmail.trim(),
        signer_name: signerName.trim() || undefined,
        signer_title: signerTitle.trim() || undefined,
        channel: 'email_otp',
        pdf_base64,
      });
      setDelivery(res.delivery);
      if (res.signing_url) setManualUrl(res.signing_url);
      setPdfFile(null);
      onAfterAction();
      void refreshStatus();
    } catch (e) {
      setError(
        e instanceof EsignError
          ? (e.message || `Send failed (${e.code}).`)
          : 'Send failed.',
      );
    } finally {
      setBusy(false);
    }
  }

  async function copyUrl() {
    if (!manualUrl) return;
    await navigator.clipboard.writeText(manualUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="rounded-md border border-tcs-border bg-tcs-bg p-4">
      <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold text-tcs-text">
        <Send className="h-4 w-4" aria-hidden /> E-Signature
      </h3>

      {isSigned ? (
        <p className="text-sm text-tcs-text-muted">
          Signed{contract.signer_name ? ` by ${contract.signer_name}` : ''}
          {contract.signed_at
            ? ` on ${new Date(contract.signed_at).toLocaleDateString()}`
            : ''}. Sealed PDF and certificate are on file.
        </p>
      ) : !canSend ? (
        <p className="text-sm text-tcs-text-muted">
          Only the contract creator, sales, national AMs, or executives can send
          for signature.
        </p>
      ) : (
        <>
          <p className="mb-3 text-xs text-tcs-text-muted">
            {isSent
              ? hasActiveToken
                ? 'A signing link is out. Re-sending replaces it.'
                : 'The last signing link is no longer active — re-send to issue a new one.'
              : 'Sends a secure signing link; the signer verifies with an emailed one-time code.'}
          </p>
          <label htmlFor="esign-email" className="block text-xs font-medium text-tcs-text-secondary">
            Signer email *
          </label>
          <input
            id="esign-email"
            type="email"
            value={signerEmail}
            onChange={(e) => setSignerEmail(e.target.value)}
            className="mb-2 mt-1 w-full rounded-md border border-tcs-border px-3 py-2 text-sm"
            placeholder="signer@client.com"
          />
          <label htmlFor="esign-name" className="block text-xs font-medium text-tcs-text-secondary">
            Signer name
          </label>
          <input
            id="esign-name"
            value={signerName}
            onChange={(e) => setSignerName(e.target.value)}
            className="mb-2 mt-1 w-full rounded-md border border-tcs-border px-3 py-2 text-sm"
          />
          <label htmlFor="esign-title" className="block text-xs font-medium text-tcs-text-secondary">
            Signer title
          </label>
          <input
            id="esign-title"
            value={signerTitle}
            onChange={(e) => setSignerTitle(e.target.value)}
            className="mb-2 mt-1 w-full rounded-md border border-tcs-border px-3 py-2 text-sm"
          />
          <label htmlFor="esign-pdf" className="block text-xs font-medium text-tcs-text-secondary">
            Contract PDF {hasActiveToken || isSent ? '(optional — replaces current)' : '*'}
          </label>
          <input
            id="esign-pdf"
            type="file"
            accept="application/pdf"
            onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
            className="mb-3 mt-1 w-full text-xs text-tcs-text-secondary"
          />
          <button
            type="button"
            onClick={send}
            disabled={busy || !signerEmail.trim()}
            className="w-full rounded-md bg-tcs-accent-ink px-4 py-2.5 text-sm font-semibold text-tcs-accent-on disabled:opacity-50"
          >
            {busy ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Sending…
              </span>
            ) : isSent ? 'Re-send for signature' : 'Send for signature'}
          </button>

          {delivery && !delivery.delivered && manualUrl && (
            <div className="mt-3 rounded-md border border-st-warn-border-strong bg-st-warn-bg p-3">
              <p className="text-xs text-st-warn-fg-strong">
                Email delivery isn’t configured yet — copy the signing link and
                deliver it to the signer yourself. (Logged in the event trail.)
              </p>
              <button
                type="button"
                onClick={copyUrl}
                className="mt-2 inline-flex items-center gap-1 rounded-md border border-st-warn-border-strong px-2 py-1 text-xs font-medium text-st-warn-fg-strong"
              >
                <Copy className="h-3 w-3" aria-hidden />
                {copied ? 'Copied' : 'Copy signing link'}
              </button>
            </div>
          )}
          {delivery?.delivered && (
            <p className="mt-2 text-xs text-st-ok-fg">Signing link emailed to {signerEmail.trim()}.</p>
          )}
          {error && (
            <div className="mt-3">
              <ErrorState title="Send failed" body={error} />
            </div>
          )}
        </>
      )}

      {events.length > 0 && (
        <div className="mt-4 border-t border-tcs-border pt-3">
          <p className="mb-2 text-xs font-semibold text-tcs-text-secondary">Event trail</p>
          <ul className="space-y-1.5">
            {events.map((ev) => (
              <li key={ev.event_id} className="flex justify-between gap-2 text-xs">
                <span className="text-tcs-text">
                  {ACTION_LABELS[ev.action] ?? ev.action}
                  {ev.signer_name ? ` — ${ev.signer_name}` : ''}
                </span>
                <span className="shrink-0 text-tcs-text-muted">
                  {new Date(ev.occurred_at).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
